import { useState, useCallback } from 'react';
import { useDropzone, Accept } from 'react-dropzone';
import classnames from 'classnames';
import { DropzoneState, FileDropzoneProps } from './types';
import { validateFiles } from './utils/validateFiles';
import s from './FileDropzone.module.scss';

const UploadCloudIcon = ({ danger }: { danger: boolean }) => {
  const color = danger
    ? 'var(--Fg-Danger-Primary-Base, #E43F3F)'
    : 'var(--Fg-Neutral-Primary-Base, #0E0E10)';

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M4 16.2422C2.79401 15.435 2 14.0602 2 12.5C2 10.1564 3.79151 8.23129 6.07974 8.01937C6.54781 5.17213 9.02024 3 12 3C14.9798 3 17.4522 5.17213 17.9203 8.01937C20.2085 8.23129 22 10.1564 22 12.5C22 14.0602 21.206 15.435 20 16.2422M16 16L12 12L8 16M12 12V21"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

function buildAcceptMap(accept: string[]): Accept {
  const map: Record<string, string[]> = {};

  for (const pattern of accept) {
    if (pattern.startsWith('.')) {
      // Extension — group under a generic MIME key that dropzone still parses
      const key = 'application/octet-stream';
      if (!map[key]) map[key] = [];
      map[key].push(pattern);
    } else {
      // MIME type (exact or wildcard)
      if (!map[pattern]) map[pattern] = [];
    }
  }

  return map as Accept;
}

export const FileDropzone = ({
  onFilesAccepted,
  onValidationError,
  multiple = false,
  maxFiles,
  maxSize,
  accept,
  disabled = false,
  currentFileCount = 0,
  hasError = false,
  dropText = 'Drop your files here or',
  browseText = 'browse files',
  hint,
}: FileDropzoneProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [hasValidationError, setHasValidationError] = useState(false);

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: { file: File }[]) => {
      setHasValidationError(false);

      // Collect files from both accepted and rejected (react-dropzone may reject
      // based on its own rules; we re-validate ourselves for a unified error surface)
      const allFiles = [...acceptedFiles, ...rejectedFiles.map((r) => r.file)];

      if (allFiles.length === 0) return;

      const errors = validateFiles(allFiles, {
        accept,
        maxSize,
        maxFiles,
        currentFileCount,
      });

      if (errors.length > 0) {
        setHasValidationError(true);
        onValidationError(errors);
        return;
      }

      onFilesAccepted(acceptedFiles);
    },
    [accept, maxSize, maxFiles, currentFileCount, onFilesAccepted, onValidationError]
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    multiple,
    disabled,
    // Pass accept only when defined to avoid react-dropzone filtering before our validator
    ...(accept != null && accept.length > 0 ? { accept: buildAcceptMap(accept) } : {}),
    // Disable react-dropzone's own size/count checks — we handle them in validateFiles
    noClick: false,
    noKeyboard: false,
  });

  // Derive visual state
  const dropzoneState: DropzoneState = (() => {
    if (disabled) return 'disabled';
    if (isDragActive) return 'dragover';
    if (hasValidationError || isDragReject) return 'danger';
    if (isHovered) return 'hovered';
    return 'enabled';
  })();

  const isDanger = dropzoneState === 'danger' || hasError;

  return (
    <div
      {...getRootProps({
        className: classnames(s.dropzone, s[`dropzone--${dropzoneState}`], hasError && s['dropzone--danger']),
        onMouseEnter: () => setIsHovered(true),
        onMouseLeave: () => setIsHovered(false),
        'aria-disabled': disabled,
      })}
      data-state={dropzoneState}
    >
      <input {...getInputProps()} />

      <div className={s.dropzone__content}>
        {/* Upload cloud icon */}
        <div className={s.dropzone__icon_wrap}>
          <UploadCloudIcon danger={isDanger} />
        </div>

        {/* Text */}
        <div className={s.dropzone__text_section}>
          <div className={s.dropzone__headline}>
            <span className={s.dropzone__headline_text}>{dropText}</span>
            <span className={s.dropzone__browse_link} role="button" tabIndex={-1}>
              {browseText}
            </span>
          </div>

          {(hint != null || (accept != null && accept.length > 0)) && (
            <span className={s.dropzone__hint}>
              {hint ?? (
                <>
                  {accept!.map(i => i.replace('.', '').toUpperCase()).join(', ')}
                  {maxSize != null && ` (max. ${(maxSize / 1_048_576).toFixed(0)} MB)`}
                </>
              )}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
